import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import CheckIcon from '@mui/icons-material/Check'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import Typography from '@mui/material/Typography'

export const PRESET_COLORS = [
  '#F44336', // Red
  '#E91E63', // Pink
  '#FF9800', // Orange
  '#FFC107', // Amber
  '#CDDC39', // Lime
  '#4CAF50', // Green
  '#009688', // Teal
  '#2196F3', // Blue
  '#607D8B', // Blue Grey
  '#795548', // Brown
] as const

export const DEFAULT_PRODUCT_COLOR = '#FF9800' satisfies (typeof PRESET_COLORS)[number]

type Props<Values extends FieldValues> = {
  name: FieldPath<Values>
  control: Control<Values>
  label?: string
  disabled?: boolean
}

export const ColorPickerController = <Values extends FieldValues>({
  name,
  control,
  label,
  disabled,
}: Props<Values>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState }) => (
        <FormControl fullWidth>
          {label && (
            <Typography variant="caption" sx={{ mb: 0.5 }}>
              {label}
            </Typography>
          )}
          <Box display="flex" flexWrap="wrap" gap={1}>
            {PRESET_COLORS.map((color) => (
              <Box
                key={color}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-pressed={value === color}
                aria-label={color}
                onClick={() => !disabled && onChange(color)}
                onKeyDown={(e) => {
                  if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    onChange(color)
                  }
                }}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  backgroundColor: color,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  border: value === color ? '2px solid #000' : '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: disabled ? 0.5 : 1,
                  '&:hover': disabled
                    ? {}
                    : {
                        opacity: 0.8,
                      },
                }}
              >
                {value === color && <CheckIcon sx={{ color: '#fff', fontSize: 20 }} />}
              </Box>
            ))}
          </Box>
          {fieldState.error?.message && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}
